import { Component, Input, OnChanges, OnDestroy, SimpleChanges, OnInit } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { AuthenticationService } from 'src/app/shared/services/authentication.service';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state/reducers';
import { SETTINGS, COMPANY, DASHBOARD, CAREERPLANNER, RELOAD } from 'src/app/app-routing.module';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { Observable, Subscription, filter, first } from 'rxjs';
import { hasValue } from 'src/app/shared/utils';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { roleMapping, User } from 'src/app/shared/types/user.types';
import { selectLastMentee } from 'src/app/state/selectors/mentee.selector';
import { TranslateService } from '@ngx-translate/core';
import { ConversationLoad } from 'src/app/state/actions/conversation.actions';

@Component({
    selector: 'fp-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: false
})
export class HeaderComponent implements OnChanges, OnDestroy, OnInit {
  unreadCount$!: Observable<number>;
  constructor(
    private authService: AuthenticationService,
    private messageService: MessageService,
    private navigationService: NavigationService,
    public notificationCenter: NotificationService,
    private store: Store<AppState>,
    private translateService: TranslateService
  ) {}
  CAREERPLANNER = CAREERPLANNER;
  COMPANY = COMPANY;
  DASHBOARD = DASHBOARD;
  RELOAD = RELOAD;
  displayConversations = false;
  label: string;
  logo;
  public menuItems: MenuItem[];
  modelSubscription: Subscription;
  photo;
  headerLoading: boolean = true;
  @Input() user: User;
  @Input() notClickableHeader: boolean = false;

  async ngOnChanges(changes: SimpleChanges) {
    this.headerLoading = true;
    this.switchLangInMenu();
    switch (this.user?.role) {
      case 'ME':
        this.updateUserRoleData('mentee');
        break;
      case 'MR':
        this.updateUserRoleData('mentor');
        break;
      case 'CH':
        this.updateUserRoleData('coach');
        break;
      case 'CC':
        this.modelSubscription = this.store
          .select(state => state.company)
          .subscribe(company => {
            this.label = company?.name.slice(0, 2).toUpperCase();
            this.headerLoading = false;
            if (company?.logo) {
              this.logo = company.logo;
            }
          });
        break;
      case 'LD':
        this.label = `${this.user?.username[0]}`.toUpperCase();
        this.headerLoading = false;
        break;
    }
  }
  switchLangInMenu() {
    this.menuItems = [
      { label: this.translateService.instant('HEADER.SETTINGS'), routerLink: SETTINGS },
      { label: this.translateService.instant('HEADER.LOG_OUT'), command: () => this.signOut() },
    ];
    if (this.user?.role == 'LD') {
      this.menuItems = [{ label: this.translateService.instant('HEADER.CAREER_PLANNER'), routerLink: CAREERPLANNER }, ...this.menuItems];
    }

    let roles = this.user?.roles;
    if (this.user?.roles?.length > 1) {
      // Filter out roles that are equal to the `user` role
      const filteredRoles = roles.filter(role => role !== 1 && roleMapping[role]?.code !== this.user?.role);
      // Create menu items based on filtered roles
      const menuItemsMoreRoles = filteredRoles.map(role => {
        const roleLabel = this.getTranslatedRoleLabel(roleMapping[role]?.code);
        const profileLabel = this.translateService.instant('HEADER.PROFILE');
        return {
          label: `${roleLabel} ${profileLabel}`,
          command: () => this.navigationService.changeUser(this.user, roleMapping[role].code),
        };
      });
      this.menuItems = [...menuItemsMoreRoles, ...this.menuItems];
    }
  }
  ngOnInit(): void {
    const hasAccessToken = !!localStorage.getItem('access_token');
    const hasUserRole = !!localStorage.getItem('user_role');

    if (hasAccessToken && hasUserRole) {
      this.store.dispatch(new ConversationLoad());
    }
    this.unreadCount$ = this.store.select(state => {
      const conversations = state.conversations || [];
      return conversations.filter(c => !c.resolved).length;
    });
    this.translateService.onLangChange.subscribe(() => {
      this.switchLangInMenu();
    });
  }

  updateUserRoleData(role) {
    const roleData$ = role === 'mentee' ? this.store.select(selectLastMentee()) : this.store.select(state => state[role]);
    // Handle the first emission and execute the "photoLoading" logic
    roleData$.pipe(filter(hasValue), first()).subscribe({
      next: roleData => {
        if (roleData?.photo) {
          this.photo = roleData.photo;
        } else {
          this.photo = undefined;
          this.label = `${roleData?.first_name[0]}${roleData?.last_name[0]}`.toUpperCase();
        }
      },
      error: () => {
        this.headerLoading = false;
      },
      complete: () => {
        this.headerLoading = false;
      },
    });

    // Continue to subscribe for further updates
    this.modelSubscription = roleData$.subscribe({
      next: roleData => {
        if (roleData?.photo) {
          this.photo = roleData.photo;
        } else {
          this.photo = undefined;
          this.label = `${roleData?.first_name[0]}${roleData?.last_name[0]}`.toUpperCase();
        }
      },
    });
  }

  onConfirm() {
    this.messageService.clear('c');
  }

  onReject() {
    this.messageService.clear('c');
  }

  clear() {
    this.messageService.clear();
  }

  navigateToRelation() {
    this.navigationService.navigateToRelation();
  }

  signOut() {
    this.authService.signOut();
  }

  toggleConversations() {
    this.displayConversations = !this.displayConversations;
  }

  getUnreadConvFromLocalStorage(): string[] {
    const unreadConvString = localStorage.getItem('unreadConv');
    return unreadConvString ? JSON.parse(unreadConvString) : [];
  }

  getTranslatedRoleLabel(roleCode: string): string {
    const roleKeyMap: { [key: string]: string } = {
      MR: 'HEADER.ROLES.MENTOR',
      CH: 'HEADER.ROLES.COACH',
      ME: 'HEADER.ROLES.MENTEE',
      AD: 'HEADER.ROLES.ADMIN',
      LD: 'HEADER.ROLES.LEAD',
      CC: 'HEADER.ROLES.COMPANY_CLIENT',
    };
    const translationKey = roleKeyMap[roleCode];
    if (translationKey) {
      return this.translateService.instant(translationKey);
    }
    // Fallback to original label from roleMapping if translation key not found
    const roleEntry = Object.values(roleMapping).find(rm => rm.code === roleCode);
    return roleEntry?.label || roleCode;
  }

  ngOnDestroy() {
    this.modelSubscription?.unsubscribe();
  }
}
